interface CacheItem {
  address: string;
  expires: number;
  timestamp: number;
}

interface CacheData {
  [key: string]: CacheItem;
}

export class GeocodingCache {
  private static CACHE_KEY = 'safee_geocoding_cache';
  private static CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 jours
  private static MAX_CACHE_SIZE = 1000;
  private static memoryCache: Map<string, CacheItem> = new Map();

  /**
   * Generate a cache key from coordinates
   * Using 6 decimal places for ~0.1m precision
   */
  private static getCacheKey(lat: number, lng: number): string {
    return `${lat.toFixed(6)},${lng.toFixed(6)}`;
  }

  /**
   * Get address from cache (memory first, then localStorage)
   */
  static get(lat: number, lng: number): string | null {
    const key = this.getCacheKey(lat, lng);
    
    // Check memory cache first
    const memoryItem = this.memoryCache.get(key);
    if (memoryItem && Date.now() < memoryItem.expires) {
      return memoryItem.address;
    }
    
    // Check localStorage cache
    try {
      const cache = this.getLocalStorageCache();
      const item = cache[key];
      
      if (item && Date.now() < item.expires) {
        // Add to memory cache for faster access
        this.memoryCache.set(key, item);
        return item.address;
      }
    } catch (error) {
      console.error('Error reading geocoding cache:', error);
    }
    
    return null;
  }

  /**
   * Set address in cache (both memory and localStorage)
   */
  static set(lat: number, lng: number, address: string): void {
    const key = this.getCacheKey(lat, lng);
    const item: CacheItem = {
      address,
      expires: Date.now() + this.CACHE_DURATION,
      timestamp: Date.now()
    };
    
    // Add to memory cache
    this.memoryCache.set(key, item);
    
    // Add to localStorage cache
    try {
      const cache = this.getLocalStorageCache();
      cache[key] = item;
      
      // Clean old entries if cache is too large
      this.cleanCache(cache);
      
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error saving to geocoding cache:', error);
    }
  }

  /**
   * Get localStorage cache data
   */
  private static getLocalStorageCache(): CacheData {
    try {
      const cacheStr = localStorage.getItem(this.CACHE_KEY);
      return cacheStr ? JSON.parse(cacheStr) : {};
    } catch (error) {
      console.error('Error parsing geocoding cache:', error);
      return {};
    }
  }

  /**
   * Clean cache by removing expired and oldest entries
   */
  private static cleanCache(cache: CacheData): void {
    const now = Date.now();
    const keys = Object.keys(cache);
    
    // Remove expired entries
    keys.forEach(key => {
      if (cache[key].expires < now) {
        delete cache[key];
      }
    });
    
    // If still too large, remove oldest entries
    const remainingKeys = Object.keys(cache);
    if (remainingKeys.length > this.MAX_CACHE_SIZE) {
      // Sort by timestamp (oldest first)
      remainingKeys.sort((a, b) => cache[a].timestamp - cache[b].timestamp);
      
      // Remove oldest entries
      const toRemove = remainingKeys.length - this.MAX_CACHE_SIZE;
      remainingKeys.slice(0, toRemove).forEach(key => {
        delete cache[key];
        this.memoryCache.delete(key); // Also remove from memory cache
      });
    }
  }

  /**
   * Clear all cache
   */
  static clear(): void {
    this.memoryCache.clear();
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.error('Error clearing geocoding cache:', error);
    }
  }

  /**
   * Get cache statistics
   */
  static getStats(): { 
    memoryCacheSize: number; 
    localStorageSize: number; 
    totalHits?: number;
    totalMisses?: number;
  } {
    const localCache = this.getLocalStorageCache();
    return {
      memoryCacheSize: this.memoryCache.size,
      localStorageSize: Object.keys(localCache).length
    };
  }

  /**
   * Check if coordinates are already cached
   */
  static isCached(lat: number, lng: number): boolean {
    return this.get(lat, lng) !== null;
  }
}